<template>
  <AppDialogContent
    v-model="dialog"
    :error="formError"
    :loading="$apollo.queries.initHorseContactForm.loading || loading"
    data-test="horseOwnerForm"
    :disabled-submit="!selectedOrManualInsert"
    @submit-form="addUpdate"
  >
    <template #header>
      {{ $t("horses:horseApplication.ownerForm.title") }}
    </template>
    <div v-show="!manuallyInserting">
      <div v-show="type == 'person'">
        <AppSelectPerson
          v-if="$auth.isAuthenticated"
          v-model="form.person"
          :input-props="{
            label: $t('horses:horseApplication.ownerForm.peopleSearch.label'),
            hint: $t('horses:horseApplication.ownerForm.peopleSearch.hint'),
            returnObject: true,
            disabled: disabledCheck('personId')
          }"
          data-test="selectPerson"
          query-with-form-data
          @returned-object="setPersonData($event)"
        />
      </div>
      <div v-show="type == 'org'">
        <AppSelectOrganisation
          v-model="form.organisation"
          :input-props="{
            label: $t(
              'horses:horseApplication.ownerForm.organisationsSearch.label'
            ),
            hint: $t(
              'horses:horseApplication.ownerForm.organisationsSearch.hint'
            ),

            disabed: disabledCheck('organisationId'),
            returnObject: true
          }"
          data-test="selectOrganisation"
          query-with-form-data
          @returned-object="setOrganisationData($event)"
        />
      </div>
    </div>

    <template v-if="selectedOrManualInsert">
      <template v-if="form.personId || form.organisationId">
        <AppShowHorseContacts
          data-test="horseContactPrefilled"
          :contacts="[form]"
        />
      </template>
      <template v-else>
        <div v-if="type == 'person'">
          <v-row dense>
            <v-col>
              <v-text-field
                v-if="v$.form.firstName"
                v-model="form.firstName"
                :label="$t('horses:horseApplication.ownerForm.firstName.label')"
                :hint="$t('horses:horseApplication.ownerForm.firstName.hint')"
                :disabled="disabledCheck('firstName')"
                data-test="firstName"
                variant="outlined"
                :error-messages="fieldErrorMessages('firstName')"
                @blur="v$.form.firstName.$touch()"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-if="v$.form.lastName"
                v-model="form.lastName"
                :label="$t('horses:horseApplication.ownerForm.lastName.label')"
                :hint="$t('horses:horseApplication.ownerForm.lastName.hint')"
                :disabled="disabledCheck('lastName')"
                data-test="lastName"
                :error-messages="fieldErrorMessages('lastName')"
                variant="outlined"
                @blur="v$.form.lastName.$touch()"
              />
            </v-col>
          </v-row>
        </div>
        <div v-else-if="type == 'org'">
          <div v-show="!form.organisationId">
            <v-text-field
              v-if="v$.form.organisationName"
              v-model="form.organisationName"
              variant="outlined"
              :error-messages="fieldErrorMessages('organisationName')"
              :label="
                $t('horses:horseApplication.ownerForm.organisationName.label')
              "
              data-test="organisationName"
              :hint="
                $t('horses:horseApplication.ownerForm.organisationName.hint')
              "
              :disabled="disabledCheck('organisationName')"
              @blur="v$.form.organisationName.$touch()"
            />
          </div>
          <v-text-field
            v-if="v$.form.organisationIdcode"
            v-model="form.organisationIdcode"
            variant="outlined"
            :error-messages="fieldErrorMessages('organisationIdcode')"
            :label="
              $t(
                'horses:horseApplication.ownerForm.ownerForm.organisationIdcode.label'
              )
            "
            data-test="organisationIdcode"
            :hint="
              $t('horses:horseApplication.ownerForm.organisationIdcode.hint')
            "
            :disabled="disabledCheck('organisationIdcode')"
            @blur="v$.form.organisationIdcode.$touch()"
          />
        </div>

        <v-text-field
          v-if="v$.form.feiid"
          v-model="form.feiid"
          :label="$t('horses:horseApplication.ownerForm.feiid.label', 'FEI ID')"
          :disabled="disabledCheck('feiid')"
          data-test="feiid"
          :error-messages="fieldErrorMessages('feiid')"
          variant="outlined"
          @blur="v$.form.feiid.$touch()"
        />

        <AppSelectCountry
          v-if="v$.form.registryCountryId"
          v-model="form.registryCountryId"
          :input-props="{
            label: $t(
              'horses:horseApplication.ownerForm.registryCountryId.label'
            ),
            hint: $t(
              'horses:horseApplication.ownerForm.registryCountryId.hint'
            ),
            errorMessages: fieldErrorMessages('registryCountryId'),
            disabled: disabledCheck('registryCountryId')
          }"
          data-test="registryCountryId"
          @blur="v$.form.registryCountryId.$touch()"
        />

        <v-row :no-gutters="$vuetify.display.xs">
          <v-col cols="12" sm="7" class="pb-0">
            <v-text-field
              v-if="v$.form.addressText"
              v-model="form.addressText"
              :label="$t('horses:horseApplication.ownerForm.addressText.label')"
              :hint="$t('horses:horseApplication.ownerForm.addressText.hint')"
              :disabled="disabledCheck('addressText')"
              data-test="addressText"
              :error-messages="fieldErrorMessages('addressText')"
              variant="outlined"
              @blur="v$.form.addressText.$touch()"
            />
          </v-col>

          <v-col cols="12" sm="5" class="pb-0">
            <v-text-field
              v-if="v$.form.addressHouse"
              v-model="form.addressHouse"
              :label="
                $t('horses:horseApplication.ownerForm.addressHouse.label')
              "
              :hint="$t('horses:horseApplication.ownerForm.addressHouse.hint')"
              :disabled="disabledCheck('addressHouse')"
              data-test="addressHouse"
              :error-messages="fieldErrorMessages('addressHouse')"
              variant="outlined"
              @blur="v$.form.addressHouse.$touch()"
            />
          </v-col>
        </v-row>

        <v-text-field
          v-if="v$.form.addressCity"
          v-model="form.addressCity"
          :label="$t('horses:horseApplication.ownerForm.addressCity.label')"
          :hint="$t('horses:horseApplication.ownerForm.addressCity.hint')"
          :disabled="disabledCheck('addressCity')"
          data-test="addressCity"
          :error-messages="fieldErrorMessages('addressCity')"
          variant="outlined"
          @blur="v$.form.addressCity.$touch()"
        />

        <v-text-field
          v-if="v$.form.addressIndex"
          v-model="form.addressIndex"
          :label="$t('horses:horseApplication.ownerForm.addressIndex.label')"
          :hint="$t('horses:horseApplication.ownerForm.addressIndex.hint')"
          :disabled="disabledCheck('addressIndex')"
          data-test="addressIndex"
          :error-messages="fieldErrorMessages('addressIndex')"
          variant="outlined"
          @blur="v$.form.addressIndex.$touch()"
        />

        <AppSelectCountry
          v-if="v$.form.addressCountryId"
          v-model="form.addressCountryId"
          :input-props="{
            label: $t('horses:horseApplication.ownerForm.addressCountry.label'),
            hint: $t('horses:horseApplication.ownerForm.addressCountry.hint'),
            errorMessages: fieldErrorMessages('addressCountryId'),
            disabled: disabledCheck('addressCountryId')
          }"
          data-test="addressCountry"
          @blur="v$.form.addressCountryId.$touch()"
        />
        <v-text-field
          v-if="v$.form.phone"
          v-model="form.phone"
          :label="$t('horses:horseApplication.ownerForm.phone.label')"
          :hint="$t('horses:horseApplication.ownerForm.phone.hint')"
          :disabled="disabledCheck('phone')"
          data-test="phone"
          :error-messages="fieldErrorMessages('phone')"
          variant="outlined"
          @blur="v$.form.phone.$touch()"
        />
        <v-text-field
          v-if="v$.form.email"
          v-model="form.email"
          :label="$t('horses:horseApplication.ownerForm.email.label')"
          :hint="$t('horses:horseApplication.ownerForm.email.hint')"
          :disabled="disabledCheck('email')"
          data-test="email"
          :error-messages="fieldErrorMessages('email')"
          variant="outlined"
          @blur="v$.form.email.$touch()"
        />
      </template>

      <v-row dense>
        <v-col>
          <AppDatePicker
            v-if="v$.form.activeFrom"
            v-model="form.activeFrom"
            :label="$t('horses:horseApplication.ownerForm.activeFrom.label')"
            :hint="$t('horses:horseApplication.ownerForm.activeFrom.hint')"
            data-test="activeFrom"
            :disabled="fieldDisabled('activeFrom')"
            :error-messages="fieldErrorMessages('activeFrom')"
            @blur="v$.form.activeFrom.$touch()"
          />
        </v-col>
        <v-col v-if="v$.form.activeUntil">
          <AppDatePicker
            v-model="form.activeUntil"
            :label="$t('horses:horseApplication.ownerForm.activeUntil.label')"
            :hint="$t('horses:horseApplication.ownerForm.activeUntil.hint')"
            data-test="activeUntil"
            :disabled="fieldDisabled('activeUntil')"
            :error-messages="fieldErrorMessages('activeUntil')"
            @blur="v$.form.activeUntil.$touch()"
          />
        </v-col>
      </v-row>
      <v-text-field
        v-if="v$.form.ownershipShare"
        v-model="form.ownershipShare"
        type="number"
        min="0.01"
        max="100.00"
        :label="$t('horses:horseApplication.ownerForm.ownershipShare.label')"
        :hint="$t('horses:horseApplication.ownerForm.ownershipShare.hint')"
        append-inner-icon="$iconPercent"
        data-test="ownershipShare"
        variant="outlined"
        :disabled="fieldDisabled('ownershipShare')"
        :error-messages="fieldErrorMessages('ownershipShare')"
        @blur="v$.form.ownershipShare.$touch()"
      />
      <template v-if="manuallyInserting && allowShowingConvertToReference">
        <template v-if="type == 'person'">
          <v-checkbox
            v-if="v$.form.convertToReference"
            v-model="form.convertToReference"
            :disabled="disabledCheck('convertToReference')"
            :label="
              $t(
                'horses:horseApplication.ownerForm.createPersonProfile.label',
                'Create person user account and profile based on contact info'
              )
            "
            :hint="
              $t(
                'horses:horseApplication.ownerForm.createPersonProfile.hint',
                'Create permanent person profile and user account based on provided information. This allows to attach several horses to the same person and allows person to log in and manage the horses'
              )
            "
            persistent-hint
            color="accent"
          />
        </template>
        <template v-else-if="type == 'org'">
          <v-checkbox
            v-if="v$.form.convertToReference"
            v-model="form.convertToReference"
            :disabled="disabledCheck('convertToReference')"
            :label="
              $t(
                'horses:horseApplication.ownerForm.createOrganisationProfile.label',
                'Create organisation record based on contact info'
              )
            "
            :hint="
              $t(
                'horses:horseApplication.ownerForm.createOrganisationProfile.hint',
                'Create permanent organisation record based on provided information. This allows to attach several horses under the same organisation. Horses can be found and managed from organsation profile page.'
              )
            "
            persistent-hint
            color="accent"
          />
        </template>
      </template>
    </template>
    <template v-else>
      <v-btn
        color="primary"
        dark
        data-test="insetContactManually"
        @click="insertingManually"
      >
        {{
          $t(
            "horses:horseApplication.ownerForm.enterDetailsManuallyButton.label",
            "Enter details manually"
          )
        }}
      </v-btn>
    </template>

    <template #submit>
      {{ $t("horses:horseApplication.ownerForm.saveButton.label", "Save") }}
    </template>
  </AppDialogContent>
</template>
<script>
import { ref, computed } from "vue";
import { injectAppAuth } from "@/composables/injections";
import gql from "fraql";
import { cloneDeep } from "lodash-es";

import { useFormValidation } from "@/composables/form/form_validation";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useCountryHelper } from "@composables/masterdata/countries";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import AppSelectOrganisation from "@AppSelects/AppSelectOrganisation.vue";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppSelectCountry from "@/components/elements/AppSelects/AppSelectCountry.vue";
import AppShowHorseContacts from "@AppShows/AppShowHorseContacts.vue";

export default {
  name: "HorseOwnerForm",
  components: {
    AppDialogContent,
    AppSelectPerson,
    AppSelectOrganisation,
    AppDatePicker,
    AppSelectCountry,
    AppShowHorseContacts
  },
  props: {
    serverSideError: {
      type: [Object, Error],
      default: null
    },
    modelValue: {
      type: Boolean,
      required: true
    },
    belongsToEntity: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    showActiveDates: {
      type: Boolean,
      default: false
    },
    allowShowingConvertToReference: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "update", "add"],
  setup() {
    const form = ref({
      contactType: null,
      rolecode: null,
      ownershipShare: null,

      person: null,
      personId: null,
      firstName: null,
      lastName: null,

      organisation: null,
      organisationId: null,
      organisationName: null,
      organisationIdcode: null,

      addressCity: null,
      addressIndex: null,
      addressText: null,
      addressHouse: null,
      addressCountryId: null,
      registryCountryId: null,
      phone: null,
      email: null
    });

    const initHorseContactForm = ref({
      vuelidateValidations: {},
      readOnlyFields: []
    });

    const validations = computed(() => {
      if (form.value.isApplicant) {
        const validations = cloneDeep(initHorseContactForm.value);
        Object.keys(validations.vuelidateValidations).forEach(valKey => {
          if (
            !["ownershipShare", "activeFrom", "activeUntil"].includes(valKey)
          ) {
            validations.vuelidateValidations[valKey] = {};
          }
        });

        return validations;
      }

      return initHorseContactForm.value;
    });
    const { v$, fieldDisabled, fieldErrorMessages, legacyFormErrorMessages } =
      useFormValidation(validations, form);

    return {
      ...useCountryHelper(),
      ...getFederationConfiguration(),

      validations,
      legacyFormErrorMessages,
      v$,
      fieldDisabled,
      fieldErrorMessages,
      form,
      initHorseContactForm
    };
  },
  data() {
    return {
      type: "person",
      formError: {},
      selectedOrManualInsert: false,
      manuallyInserting: false
    };
  },
  apollo: {
    initHorseContactForm: {
      query: gql`
        query initHorseContactForm(
          $belongsToEntity: String!
          $contactType: String
        ) {
          initHorseContactForm(
            belongsToEntity: $belongsToEntity
            contactType: $contactType
          ) {
            readOnlyFields
            vuelidateValidations
          }
        }
      `,
      skip() {
        return !this.form.contactType;
      },
      variables() {
        let contactType = this.form.contactType;
        if (this.manuallyInserting) {
          switch (this.type) {
            case "person":
              contactType = "PERSON_INFO";
              break;
            case "org":
              contactType = "ORGANISATION_INFO";
          }
        }
        return {
          belongsToEntity: this.belongsToEntity,
          contactType: contactType
        };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        this.initHorseContactForm = data.initHorseContactForm;
      },
      fetchPolicy: "network-only"
    }
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      }
    },
    disabled() {
      return Boolean(
        this.form.isApplicant || this.form.personId || this.form.organisationId
      );
    }
  },
  watch: {
    serverSideError(newVal) {
      this.formError = newVal;
    },
    dialog(v) {
      this.formError = {};
      if (v === false) {
        this.selectedOrManualInsert = false;
        this.manuallyInserting = false;
        this.form = {
          contactType: null,
          rolecode: null,
          ownershipShare: null,

          person: null,
          personId: null,
          firstName: null,
          lastName: null,

          feiid: null,

          organisation: null,
          organisationId: null,
          organisationName: null,
          organisationIdcode: null,

          addressCity: null,
          addressIndex: null,
          addressText: null,
          addressHouse: null,
          addressCountryId: null,
          registryCountryId: null,
          phone: null,
          email: null
        };
        this.$forceUpdate();
      }
    },
    "form.personId"(v) {
      if (this.form.isApplicant === true && v != this.$auth.user.id) {
        this.isApplicant = false;
      }
    }
  },
  methods: {
    clearOut() {
      this.form = {
        contactType: null,
        rolecode: null,
        ownershipShare: null,

        person: null,
        personId: null,
        firstName: null,
        lastName: null,

        feiid: null,

        organisation: null,
        organisationId: null,
        organisationName: null,
        organisationIdcode: null,

        addressCity: null,
        addressIndex: null,
        addressText: null,
        addressHouse: null,
        addressCountryId: null,
        registryCountryId: null,
        phone: null,
        email: null
      };

      this.selectedOrManualInsert = false;
      this.$forceUpdate();
    },
    insertingManually() {
      this.v$.form.$reset();
      this.selectedOrManualInsert = true;
      this.manuallyInserting = true;
    },
    disabledCheck(fieldName) {
      return (
        this.fieldDisabled(fieldName) ||
        ([
          "personId",
          "organisationId",
          "ownershipShare",
          "activeFrom",
          "activeUntil"
        ].includes(fieldName)
          ? false
          : this.disabled)
      );
    },
    setItemData(item) {
      this.form = JSON.parse(JSON.stringify(item));
      if (
        this.form.isApplicant ||
        this.form.personId ||
        this.form.organisationId ||
        this.form.firstName ||
        this.form.organisationName ||
        (item.contactType == "PERSON_INFO" && !this.$auth.isAuthenticated)
      ) {
        this.selectedOrManualInsert = true;
        if (
          (!this.form.personId && !this.form.organisationId) ||
          (item.contactType == "PERSON_INFO" && !this.$auth.isAuthenticated)
        ) {
          this.manuallyInserting = true;
        }
      }

      switch (item.contactType) {
        case "PERSON_REFERENCE":
        case "PERSON_INFO":
          this.type = "person";
          break;
        case "ORGANISATION_REFERENCE":
        case "ORGANISATION_INFO":
          this.type = "org";
          break;
      }
      this.$forceUpdate();
    },
    setPersonData(person) {
      if (!person) {
        this.clearOut();
        return;
      }
      // All data is coming from AppSelectPerson
      this.form.person = person;
      this.form.personId = person.id;
      this.form.firstName = person.firstName;
      this.form.lastName = person.lastName;
      this.form.feiid = person.feiid;
      this.form.registryCountryId = person.registryCountryId;
      this.form.addressCity = person.address?.addressCity ?? null;
      this.form.addressIndex = person.address?.addressIndex ?? null;
      this.form.addressText = person.address?.addressText ?? null;
      this.form.addressHouse = person.address?.addressHouse ?? null;
      this.form.addressCountryId = person.address?.addressCountryId ?? null;
      this.form.phone = person.phone;
      this.form.email = person.email;
      this.selectedOrManualInsert = true;
      this.$forceUpdate();
    },
    setOrganisationData(org) {
      if (!org) {
        this.clearOut();
        return;
      }
      // All data is coming from AppSelectOrganisation
      this.form.organisation = org;
      this.form.organisationId = org.id;
      this.form.organisationName = org.name;
      this.form.organisationIdcode = org.businessRegCode;
      this.form.feiid = org.feiid;
      this.form.registryCountryId = org.registryCountryId;
      this.form.addressCity = org.address?.addressCity ?? null;
      this.form.addressIndex = org.address?.addressIndex ?? null;
      this.form.addressText = org.address?.addressText ?? null;
      this.form.addressHouse = org.address?.addressHouse ?? null;
      this.form.addressCountryId = org.address?.addressCountryId ?? null;
      this.form.phone = org.phone;
      this.form.email = org.email;
      this.selectedOrManualInsert = true;
      this.$forceUpdate();
    },
    async addUpdate() {
      this.formError = {};
      if (this.form.ownershipShare)
        this.form.ownershipShare = parseFloat(this.form.ownershipShare);
      if (!this.form.isApplicant || !this.form.id) {
        switch (this.type) {
          case "person":
            if (this.form.personId) this.form.contactType = "PERSON_REFERENCE";
            else this.form.contactType = "PERSON_INFO";
            break;

          case "org":
            if (this.form.organisationId)
              this.form.contactType = "ORGANISATION_REFERENCE";
            else this.form.contactType = "ORGANISATION_INFO";
            break;
        }
      }
      this.form.rolecode = "OWNER";
      this.v$.form.$touch();

      if (this.v$.form.$invalid) {
        this.formError = this.legacyFormErrorMessages();
        return false;
      }

      this.form.addressCountry = this.getCountryFromMaster(
        this.form.addressCountryId
      );
      this.form.registryCountry = this.getCountryFromMaster(
        this.form.registryCountry
      );

      if (this.form.id || this.form.indexId != null) {
        this.$emit("update", this.form);
      } else {
        this.$emit("add", this.form);
      }
      this.v$.form.$reset();
    }
  }
};

export const useHorseContactStarter = (horseContactForm, formDialogRef) => {
  const internalContactIdCount = ref(1);
  const horseContactModal = ref(false);

  const { appAuth } = injectAppAuth();

  const applicantIsAddedAsContact = computed(() => {
    return Boolean(
      horseContactForm.value.horseContacts.find(x => x.isApplicant === true)
    );
  });

  /**
   * Find applicant data by isApplicant and set fields to exact same as applicant* are
   * @param {*} applicant
   * @param {*} horseContacts
   */
  function mutateApplicantHorseContact(applicant, horseContacts) {
    const applicantIndex = horseContacts.findIndex(x => x.isApplicant);
    if (applicantIndex == -1) return;

    horseContacts[applicantIndex].firstName = applicant.applicantFirstName;
    horseContacts[applicantIndex].lastName = applicant.applicantLastName;
    horseContacts[applicantIndex].feiid = applicant.applicantFeiid;
    horseContacts[applicantIndex].addressCity = applicant.applicantAddressCity;
    horseContacts[applicantIndex].addressIndex =
      applicant.applicantAddressIndex;
    horseContacts[applicantIndex].addressText = applicant.applicantAddressText;
    horseContacts[applicantIndex].addressHouse =
      applicant.applicantAddressHouse;
    horseContacts[applicantIndex].addressCountryId =
      applicant.applicantAddressCountryId;
    horseContacts[applicantIndex].registryCountryId =
      applicant.applicantRegistryCountryId;
    horseContacts[applicantIndex].phone = applicant.applicantPhone;
    horseContacts[applicantIndex].email = applicant.applicantEmail;
  }

  function addApplicantAsHorseContact(contactForm, person) {
    if (appAuth.isAuthenticated) {
      formDialogRef.value.setItemData({
        contactType: "PERSON_REFERENCE",
        personId: person.id,
        person: person,
        firstName: person.firstName,
        lastName: person.lastName,
        feiid: person.feiid,
        addressCity: person.address?.addressCity ?? null,
        addressIndex: person.address?.addressIndex ?? null,
        addressText: person.address?.addressText ?? null,
        addressHouse: person.address?.addressHouse ?? null,
        addressCountryId: person.address?.addressCountryId ?? null,
        registryCountryId: person.registryCountryId,
        email: person.email,
        phone: person.phone,
        isApplicant: true,
        rolecode: null
      });
    } else {
      formDialogRef.value.setItemData({
        contactType: "PERSON_INFO",
        firstName: contactForm.applicantFirstName,
        lastName: contactForm.applicantLastName,
        feiid: contactForm.applicantFeiid,
        addressCity: contactForm.applicantAddressCity,
        addressIndex: contactForm.applicantAddressIndex,
        addressText: contactForm.applicantAddressText,
        addressHouse: this.person.applicantAddressHouse,
        addressCountryId: contactForm.applicantCountryId,
        email: contactForm.applicantEmail,
        phone: contactForm.applicantPhone,
        registryCountryId: contactForm.applicantRegistryCountryId,
        isApplicant: true,
        rolecode: null
      });
    }

    horseContactModal.value = true;
  }

  function addPersonAsHorseContact() {
    formDialogRef.value.setItemData({
      contactType: appAuth.isAuthenticated ? "PERSON_REFERENCE" : "PERSON_INFO"
    });
    horseContactModal.value = true;
  }

  function addOrganisationAsHorseContact() {
    formDialogRef.value.setItemData({
      contactType: "ORGANISATION_REFERENCE"
    });
    horseContactModal.value = true;
  }

  function addHorseContact(form) {
    form.indexId = internalContactIdCount.value;
    internalContactIdCount.value++;
    horseContactForm.value.horseContacts = [
      ...horseContactForm.value.horseContacts,
      form
    ];
    horseContactModal.value = false;
  }

  function editHorseContact(item) {
    formDialogRef.value.setItemData(item);
    horseContactModal.value = true;
  }

  function updateHorseContact(form) {
    let i = horseContactForm.value.horseContacts.findIndex(x =>
      form.id ? x.id == form.id : x.indexId == form.indexId
    );
    horseContactForm.value.horseContacts.splice(i, 1, form);
    horseContactModal.value = false;
  }

  function removeHorseContact(item) {
    horseContactForm.value.horseContacts =
      horseContactForm.value.horseContacts.filter(x =>
        item.id ? x.id != item.id : x.indexId != item.indexId
      );
  }

  /**
   * Clear out fields that HorseContactType in server doesn't like
   * @param {*} contacts
   */
  function horseContactsClearOutJunkFields(contacts, validationObject) {
    return contacts.map(x => {
      delete x.indexId;
      delete x.isApplicant;
      delete x.organisation;
      delete x.person;
      delete x.addressCountry;
      delete x.__typename;
      if (!validationObject.activeUntil) {
        delete x.activeUntil;
      }
      return x;
    });
  }

  return {
    internalContactIdCount,
    horseContactModal,

    applicantIsAddedAsContact,

    mutateApplicantHorseContact,
    addApplicantAsHorseContact,
    addPersonAsHorseContact,
    addOrganisationAsHorseContact,
    addHorseContact,
    editHorseContact,
    updateHorseContact,
    removeHorseContact,
    horseContactsClearOutJunkFields
  };
};
</script>
